// The music of the game, as the screens see it: tell it the scene (the menu, a race and where it stands, a workout
// block, the results) and it plays the right piece at the right intensity, through the director's hysteresis and the
// music volume of the settings. It starts only after the first gesture on the page (browsers allow no sound before),
// fades out when a menu's tab is hidden (a ride's music goes on with the ride) and resumes when a volume moves.
import { state } from '../../data/store.js';
import { audio, mixLevels } from '../engine.js';
import { Director, SILENT, type Scene } from './director.js';
import { MusicPlayer } from './player.js';

/** How often the next bars are scheduled (ms): under the lookahead, even once the browser slows a hidden page's timers. */
const TICK_MS = 250;

/** The part of the page the music watches: a test passes its own. */
export interface Page {
  hidden: boolean;
  addEventListener(type: string, fn: () => void, opts?: AddEventListenerOptions): void;
}

export class Music {
  private scene: Scene = { kind: 'silent' };
  private dir = new Director();
  private player: MusicPlayer | null = null;
  /** A user gesture happened: the audio context may run. */
  private armed = false;
  private hidden = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private now: () => number;

  constructor(now: () => number = () => performance.now() / 1000) { this.now = now; }

  /** Follows the page: the first gesture arms the audio, hiding the tab silences a menu's music. */
  attach(page: Page) {
    const arm = () => { this.armed = true; this.apply(); };
    page.addEventListener('pointerdown', arm, { once: true, passive: true } as AddEventListenerOptions);
    page.addEventListener('keydown', arm, { once: true, passive: true } as AddEventListenerOptions);
    page.addEventListener('visibilitychange', () => { this.hidden = page.hidden; this.apply(); });
    this.hidden = page.hidden;
  }

  /** What is on screen now. Cheap: the screens call it every frame of a ride. */
  set(scene: Scene) { this.scene = scene; this.apply(); }

  /** The settings changed (a volume moved). */
  refresh() { this.apply(); }

  /** Schedules the next bars; the ride's step calls it so a hidden ride never runs out of music. */
  tick() { this.player?.tick(this.hidden); }

  /** The piece playing, for the tests and the tools. */
  get playing(): string | null { return this.player?.playing ?? null; }

  private apply() {
    const cue = this.dir.step(this.scene, this.now());
    if (!this.armed) return;
    const L = mixLevels(state.settings);
    // a hidden menu is silent; a hidden ride keeps its music as it keeps its sound
    const want = !!cue.song && L.music > 0 && !(this.hidden && this.scene.kind !== 'ride');
    if (!want) { this.player?.cue(SILENT); this.stopTimer(); return; }
    const b = audio();
    if (!b) return;
    this.player ??= new MusicPlayer(b);
    this.player.cue(cue);
    if (!this.timer) this.timer = setInterval(() => this.tick(), TICK_MS);
  }

  private stopTimer() { if (this.timer) { clearInterval(this.timer); this.timer = null; } }
}

export const music = new Music();
